<template>
  <div class="roots-of-unity-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$z^n = 1 \implies z_k = \cos\frac{2k\pi}{n} + i\sin\frac{2k\pi}{n}$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="340" viewBox="0 0 600 400" preserveAspectRatio="xMidYMid meet">
          <!-- 投影滤镜 -->
          <defs>
            <filter id="glow-root">
              <feGaussianBlur stdDeviation="2.5" result="blur"/>
              <feComposite in="SourceGraphic" in2="blur" operator="over"/>
            </filter>
          </defs>

          <!-- 坐标轴 -->
          <g transform="translate(300, 200)">
            <line x1="-180" y1="0" x2="180" y2="0" stroke="rgba(255,255,255,0.1)" stroke-width="1" />
            <line x1="0" y1="-180" x2="0" y2="180" stroke="rgba(255,255,255,0.1)" stroke-width="1" />
            <circle cx="0" cy="0" :r="radius" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="2" stroke-dasharray="5,3" />

            <!-- 单位根连线 (形成正n边形) -->
            <polygon :points="polygonPoints" fill="none" stroke="rgba(34, 211, 238, 0.2)" stroke-width="2" />

            <!-- 单位根点与向量 -->
            <g v-for="(root, i) in roots" :key="i">
              <line x1="0" y1="0" :x2="root.x" :y2="root.y" :stroke="rootColor(i)" stroke-width="2.5" opacity="0.6" />
              <circle :cx="root.x" :cy="root.y" r="6" :fill="rootColor(i)" filter="url(#glow-root)" />
              <text :x="root.x * 1.15 - 10" :y="root.y * 1.15 + 5" fill="#fff" font-size="12" font-weight="900">ω{{ i }}</text>
            </g>
          </g>
        </svg>

        <div class="hud-top">
          <div class="hud-item cyan">
            <label>方程次幂 (n)</label>
            <span class="val">{{ n }} 次单位根</span>
          </div>
          <div class="hud-item orange" v-if="n > 1">
            <label>和的性质 (Σω_k)</label>
            <span class="val">0 (几何中心)</span>
          </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
          <div class="s-row">
            <label>调节单位根个数 (n = {{ n }})</label>
            <el-slider v-model="n" :min="2" :max="12" :step="1"></el-slider>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
          <i class="el-icon-sunny"></i>
          <p>
            <strong>核心揭示：</strong>$n$ 次单位根在复平面上恰好构成单位圆内接正 $n$ 边形。
            <strong>代数特性：</strong>根据对称性，其所有根之和必为 0（即 $\sum_{k=0}^{n-1} \omega_k = 0$）。
            在解方程 $z^n = a$ 时，只需找到一个特解，其余解均可通过乘以单位根旋转得到。
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'RootsOfUnityViz',
  data() {
    return {
      n: 6,
      radius: 140
    }
  },
  computed: {
    roots() {
      const result = [];
      for (let k = 0; k < this.n; k++) {
        const theta = (2 * Math.PI * k) / this.n;
        result.push({
          x: this.radius * Math.cos(theta),
          y: -this.radius * Math.sin(theta) // Mirror for SVG Y-axis
        });
      }
      return result;
    },
    polygonPoints() {
      return this.roots.map(r => `${r.x},${r.y}`).join(' ');
    }
  },
  methods: {
    rootColor(i) {
      if (i === 0) return '#fbbf24'; // Primary root (1,0)
      const hue = (360 / this.n) * i;
      return `hsl(${hue}, 80%, 65%)`;
    }
  }
}
</script>

<style scoped lang="scss">
.roots-of-unity-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 700; } .formula-chip { background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; padding: 4px 14px; border-radius: 20px; font-weight: 800; font-family: monospace; font-size: 0.8rem; } }
.simulation-stage { min-height: 340px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-top { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 800; } .val { font-size: 13px; color: #f8fafc; font-weight: 900; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #fff; padding: 10px 15px; border-radius: 12px; border: 1px solid #e2e8f0; .s-row { label { display: block; font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; } } }
.discovery-card-compact { background: #f0fdf4; padding: 10px 14px; border-radius: 10px; border: 1px solid #bbf7d0; display: flex; align-items: center; gap: 10px; i { color: #16a34a; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 11.5px; color: #166534; line-height: 1.5; strong { color: #064e3b; } } }
</style>
